/**
 * uni.request 二次封装
 * - 统一 baseURL / token / 错误处理 / 类型化
 * - Access Token 过期时自动无痕 Refresh（队列防并发）
 * - 错误展示用 status 码做 i18n，不依赖后端 message
 */

import { API_BASE_URL } from '@/config/env'
import { REQUEST_TIMEOUT } from '@/config/constants'
import { BizCode } from '@/config/enums'
import { getToken, clearAuthSession } from '@/utils/auth'
import { trySilentRefresh } from '@/utils/refresh'
import { getDeviceId } from '@/utils/device'
import { hideToast, showToast } from '@/utils/platform'
import { tBizStatus } from '@/utils/i18n'
import { consumeRateLimitToast, isRateLimitCooling, markRateLimited } from '@/utils/rate-limit-guard'

/** 业务层 method（含 PATCH）；uni 旧类型缺 PATCH，调用时再收窄 */
export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** uni.request 声明里的 method 联合（缺 PATCH，H5 运行时可用） */
type UniRequestMethod = NonNullable<UniNamespace.RequestOptions['method']>

function toUniMethod(method: RequestMethod | undefined): UniRequestMethod {
  return (method ?? 'GET') as UniRequestMethod
}

function syncUserStoreAfterLogout(): void {
  void import('@/store/modules/user').then(({ useUserStore }) => {
    useUserStore().$patch({
      token: '',
      hasRefresh: false,
      userInfo: null,
      sessionReady: true,
    })
  })
}

/** 并发 401 只踢一次，避免多次 reLaunch */
let kickingUnauthorized = false

function handleUnauthorized(silent?: boolean, message?: string): void {
  clearAuthSession()
  syncUserStoreAfterLogout()
  if (kickingUnauthorized) return
  kickingUnauthorized = true
  if (!silent) showToast(message ?? tBizStatus('AUTH_SESSION_EXPIRED'))
  // reLaunch 前清 toast，避免 H5 全局层粘到首页（WEB-A02）
  hideToast()
  uni.reLaunch({
    url: '/pages/index/index',
    complete: () => {
      kickingUnauthorized = false
    },
  })
}

/** softAuth：清本地会话但不 reLaunch，交给页面 */
function clearSessionSoft(): void {
  clearAuthSession()
  syncUserStoreAfterLogout()
}

export interface RequestOptions {
  url: string
  method?: RequestMethod
  data?: unknown
  header?: Record<string, string>
  timeout?: number
  /** 是否跳过统一错误提示 */
  silent?: boolean
  /** 是否需要登录态（默认 true） */
  withAuth?: boolean
  /** 401 时不踢回首页，由页面自行处理（详情页等） */
  softAuth?: boolean
  /** 内部标记：是否已尝试 refresh */
  _retried?: boolean
}

export interface BizResponse<T> {
  code: number
  /** 业务状态码（i18n 主键） */
  status: string
  message: string
  data: T
  details?: Record<string, unknown>
}

function buildHeader(opts: RequestOptions): Record<string, string> {
  const header: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Device-Id': getDeviceId(),
    ...(opts.header ?? {}),
  }
  if (opts.withAuth !== false) {
    const token = getToken()
    if (token) header.Authorization = `Bearer ${token}`
  }
  // 行情 / 持仓 / K 线：禁止浏览器长期缓存旧 JSON（避免「接口已新、页面还旧」）
  const path = opts.url || ''
  const method = (opts.method ?? 'GET').toUpperCase()
  if (
    method === 'GET' &&
    (path.includes('/quotes') || path.includes('/content/home') || path.includes('/content/holdings'))
  ) {
    header['Cache-Control'] = 'no-cache'
    header.Pragma = 'no-cache'
  }
  return header
}

function handleBizError(res: BizResponse<unknown>, silent?: boolean): void {
  const text = tBizStatus(res.status, res.message)
  if (res.status === 'RATE_LIMIT_EXCEEDED') {
    markRateLimited()
    if (!silent && consumeRateLimitToast()) showToast(text)
    return
  }
  if (res.code === BizCode.UNAUTHORIZED) {
    handleUnauthorized(silent, text)
    return
  }
  if (!silent) showToast(text)
}

export function request<T>(options: RequestOptions): Promise<T> {
  return new Promise((resolve, reject) => {
    // 限流冷却期：本地短路，不再发网络请求
    if (isRateLimitCooling()) {
      if (!options.silent && consumeRateLimitToast()) showToast(tBizStatus('RATE_LIMIT_EXCEEDED'))
      reject({ code: 429, status: 'RATE_LIMIT_EXCEEDED', message: '', data: null } as BizResponse<T>)
      return
    }
    uni.request({
      url: API_BASE_URL + options.url,
      method: toUniMethod(options.method),
      data: options.data as Record<string, unknown> | undefined,
      header: buildHeader(options),
      timeout: options.timeout ?? REQUEST_TIMEOUT,
      success: (response) => {
        void (async () => {
          const status = response.statusCode
          if (status < 200 || status >= 300) {
            if (status === 429) {
              markRateLimited()
              if (!options.silent && consumeRateLimitToast()) showToast(tBizStatus('RATE_LIMIT_EXCEEDED'))
            } else if (!options.silent) {
              showToast(`网络错误 ${status}`)
            }
            reject(new Error(`HTTP ${status}`))
            return
          }
          const body = response.data as BizResponse<T>
          if (body.code === BizCode.UNAUTHORIZED && options.withAuth !== false && !options._retried) {
            const refreshResult = await trySilentRefresh()
            if (refreshResult === 'ok') {
              try {
                const data = await request<T>({ ...options, _retried: true })
                resolve(data)
              } catch (e) {
                reject(e)
              }
              return
            }
            // 网络抖动：不清 token、不踢登，避免误下线
            if (refreshResult === 'network') {
              if (!options.silent) showToast(tBizStatus('SERVER_ERROR'))
              reject(new Error('AUTH_REFRESH_NETWORK'))
              return
            }
            // invalid / none：会话已死
            if (options.softAuth) {
              clearSessionSoft()
              reject(body)
              return
            }
            handleBizError(body, options.silent)
            reject(body)
            return
          }
          if (body.code !== BizCode.SUCCESS) {
            if (body.code === BizCode.UNAUTHORIZED && options.softAuth) {
              clearSessionSoft()
              reject(body)
              return
            }
            handleBizError(body, options.silent)
            reject(body)
            return
          }
          resolve(body.data)
        })()
      },
      fail: (err) => {
        if (!options.silent) showToast(tBizStatus('SERVER_ERROR'))
        reject(err)
      },
    })
  })
}
